import { create } from 'zustand';
import { whiteLabelApi, WhiteLabelSettings } from '@/api/whiteLabel';

// Cores padrão do FeedBatch
export const DEFAULT_PRIMARY_COLOR = '#E94B60';
export const DEFAULT_SECONDARY_COLOR = '#22212C';
export const DEFAULT_ACCENT_COLOR = '#E94B60';
export const DEFAULT_COMPANY_NAME = 'FeedBatch';
export const DEFAULT_HEADER_TITLE = 'Seja mais produtivo automatizando tarefas! - Feedbatch - Ganhe mais tempo e visibilidade com nossas integrações';

interface WhiteLabelState {
  settings: WhiteLabelSettings | null;
  isLoaded: boolean;
  isLoading: boolean;

  // Derived values (atualizam o state para trigger re-render)
  companyName: string;
  headerTitle: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;

  // Actions
  loadSettings: () => Promise<void>;
  loadPublicSettings: () => Promise<void>;
  updateSettings: (settings: Partial<WhiteLabelSettings>) => void;
  applyTheme: () => void;
}

function deriveValues(settings: WhiteLabelSettings | null) {
  return {
    companyName: settings?.companyName || DEFAULT_COMPANY_NAME,
    headerTitle: settings?.headerTitle || DEFAULT_HEADER_TITLE,
    logoUrl: settings?.logoUrl || null,
    primaryColor: settings?.primaryColor || DEFAULT_PRIMARY_COLOR,
    secondaryColor: settings?.secondaryColor || DEFAULT_SECONDARY_COLOR,
    accentColor: settings?.accentColor || DEFAULT_ACCENT_COLOR,
  };
}

export const useWhiteLabelStore = create<WhiteLabelState>((set, get) => ({
  settings: null,
  isLoaded: false,
  isLoading: false,

  // Valores derivados iniciais
  companyName: DEFAULT_COMPANY_NAME,
  headerTitle: DEFAULT_HEADER_TITLE,
  logoUrl: null,
  primaryColor: DEFAULT_PRIMARY_COLOR,
  secondaryColor: DEFAULT_SECONDARY_COLOR,
  accentColor: DEFAULT_ACCENT_COLOR,

  loadSettings: async () => {
    if (get().isLoading) return;
    
    set({ isLoading: true });
    try {
      const settings = await whiteLabelApi.getSettings();
      set({ settings, isLoaded: true, isLoading: false, ...deriveValues(settings) });
      get().applyTheme();
    } catch (error) {
      console.error('Failed to load white-label settings:', error);
      set({ isLoaded: true, isLoading: false });
    }
  },

  loadPublicSettings: async () => {
    if (get().isLoaded || get().isLoading) return;
    
    set({ isLoading: true });
    try {
      const settings = await whiteLabelApi.getPublicSettings();
      set({ settings, isLoaded: true, isLoading: false, ...deriveValues(settings) });
      get().applyTheme();
    } catch (error) {
      console.error('Failed to load public white-label settings:', error);
      set({ isLoaded: true, isLoading: false });
    }
  },

  updateSettings: (newSettings) => {
    const current = get().settings;
    const merged = { ...current, ...newSettings } as WhiteLabelSettings;
    set({ settings: merged, ...deriveValues(merged) });
    get().applyTheme();
  },

  applyTheme: () => {
    const { settings } = get();
    const root = document.documentElement;

    const primary = settings?.primaryColor || DEFAULT_PRIMARY_COLOR;
    const secondary = settings?.secondaryColor || DEFAULT_SECONDARY_COLOR;
    const accent = settings?.accentColor || DEFAULT_ACCENT_COLOR;

    root.style.setProperty('--wl-primary', primary);
    root.style.setProperty('--wl-secondary', secondary);
    root.style.setProperty('--wl-accent', accent);
    root.style.setProperty('--wl-primary-light', `${primary}14`);
    root.style.setProperty('--wl-primary-hover', darkenColor(primary, 10));
    root.style.setProperty('--wl-accent-hover', darkenColor(accent, 10));
    root.style.setProperty('--wl-accent-light', `${accent}14`);
    root.style.setProperty('--wl-text-muted', hexToRgba(secondary, 0.6));
  },
}));

/**
 * Escurece uma cor hex em uma porcentagem
 */
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max((num >> 16) - amt, 0);
  const G = Math.max(((num >> 8) & 0x00ff) - amt, 0);
  const B = Math.max((num & 0x0000ff) - amt, 0);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

/**
 * Converte cor hex para rgba com opacidade
 */
function hexToRgba(hex: string, alpha: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const R = (num >> 16) & 0xff;
  const G = (num >> 8) & 0xff;
  const B = num & 0xff;
  return `rgba(${R}, ${G}, ${B}, ${alpha})`;
}
